/**
 * Research Rank → ResearchModifiers (pure).
 *
 * Ordering (document in RESEARCH_MODEL.md):
 *   1. Author authored TowerDefinition.levels[towerLevel-1] (match LV1–LV4)
 *   2. Apply ResearchModifiers from snapshotted ResearchState
 *   3. ResolvedTowerStats feed combat / inspect / targeting
 *
 * Research never mutates TargetClass. Durations stay identity (statusDurationMultiplier = 1).
 */

import {
  IDENTITY_RESEARCH,
  RESEARCH_MAX_RANK,
  type ResearchModifiers,
  type ResearchTowerId,
  type TowerResearchRanks
} from './types'

/** POWER damage bonus per rank for FIRE / CANNON / AA (and shared RANGE/TEMPO). */
export const POWER_DAMAGE_PER_RANK = 0.05
/** RANGE cells multiplier per rank → +12% at rank 3. */
export const RANGE_PER_RANK = 0.04
/** Attack-rate bonus per TEMPO rank; interval *= 1/(1+rate). Rank 5 ≈ +20% rate. */
export const TEMPO_RATE_PER_RANK = 0.04

/** Frost POWER: modest damage + slow strength (not duration). */
export const FROST_POWER_DAMAGE_PER_RANK = 0.03
export const FROST_SLOW_BONUS_PER_RANK = 0.012
/** Hard cap on frost effect slow after Research (Deep Freeze untouched). */
export const FROST_SLOW_RESEARCH_CAP = 0.34

/** Poison POWER: primarily DoT; modest impact damage. */
export const POISON_DOT_PER_RANK = 0.05
export const POISON_IMPACT_DAMAGE_PER_RANK = 0.02

/** Mage POWER: primarily Armor Break magnitude; modest direct damage. */
export const MAGE_ARMOR_BREAK_PER_RANK = 0.04
export const MAGE_DAMAGE_PER_RANK = 0.02
/** Hard cap after Research so armor never fully deleted by Research alone. */
export const ARMOR_BREAK_RESEARCH_CAP = 0.85

function clampRank(rank: number, max: number): number {
  if (!Number.isFinite(rank)) return 0
  return Math.max(0, Math.min(max, Math.floor(rank)))
}

function rangeMultiplier(rangeRank: number): number {
  return 1 + RANGE_PER_RANK * clampRank(rangeRank, RESEARCH_MAX_RANK.range)
}

function tempoIntervalMultiplier(tempoRank: number): number {
  const r = clampRank(tempoRank, RESEARCH_MAX_RANK.tempo)
  return 1 / (1 + TEMPO_RATE_PER_RANK * r)
}

/**
 * Build combat ResearchModifiers for one tower from its ranks.
 * Pure / deterministic. Does not read storage.
 */
export function researchModifiersForTower(
  towerId: ResearchTowerId,
  ranks: TowerResearchRanks
): ResearchModifiers {
  const power = clampRank(ranks.power, RESEARCH_MAX_RANK.power)
  const range = clampRank(ranks.range, RESEARCH_MAX_RANK.range)
  const tempo = clampRank(ranks.tempo, RESEARCH_MAX_RANK.tempo)

  const base: ResearchModifiers = {
    ...IDENTITY_RESEARCH,
    rangeMultiplier: rangeMultiplier(range),
    attackIntervalMultiplier: tempoIntervalMultiplier(tempo),
    statusDurationMultiplier: 1
  }

  switch (towerId) {
    case 'fire':
    case 'cannon':
    case 'aa':
      return {
        ...base,
        damageMultiplier: 1 + POWER_DAMAGE_PER_RANK * power
      }
    case 'frost':
      return {
        ...base,
        damageMultiplier: 1 + FROST_POWER_DAMAGE_PER_RANK * power,
        frostSlowBonus: FROST_SLOW_BONUS_PER_RANK * power
      }
    case 'poison':
      return {
        ...base,
        damageMultiplier: 1 + POISON_IMPACT_DAMAGE_PER_RANK * power,
        poisonDotMultiplier: 1 + POISON_DOT_PER_RANK * power
      }
    case 'mage':
      return {
        ...base,
        damageMultiplier: 1 + MAGE_DAMAGE_PER_RANK * power,
        armorBreakMagnitudeMultiplier: 1 + MAGE_ARMOR_BREAK_PER_RANK * power
      }
    default: {
      const _exhaustive: never = towerId
      return _exhaustive
    }
  }
}

/** Player-facing POWER summary percentages (aligned with combat formulas). */
export function powerEffectLabel(towerId: ResearchTowerId, rank: number): string {
  const r = clampRank(rank, RESEARCH_MAX_RANK.power)
  if (r <= 0) return 'No Power research'
  switch (towerId) {
    case 'fire':
    case 'cannon':
    case 'aa':
      return `+${Math.round(POWER_DAMAGE_PER_RANK * r * 100)}% damage`
    case 'frost':
      return `+${Math.round(FROST_POWER_DAMAGE_PER_RANK * r * 100)}% damage, +${(FROST_SLOW_BONUS_PER_RANK * r * 100).toFixed(1)}% slow`
    case 'poison':
      return `+${Math.round(POISON_DOT_PER_RANK * r * 100)}% Poison DoT, +${Math.round(POISON_IMPACT_DAMAGE_PER_RANK * r * 100)}% impact`
    case 'mage':
      return `+${Math.round(MAGE_ARMOR_BREAK_PER_RANK * r * 100)}% Armor Break, +${Math.round(MAGE_DAMAGE_PER_RANK * r * 100)}% damage`
    default: {
      const _exhaustive: never = towerId
      return _exhaustive
    }
  }
}

export function rangeEffectLabel(rank: number): string {
  const r = clampRank(rank, RESEARCH_MAX_RANK.range)
  if (r <= 0) return 'No Range research'
  return `+${Math.round(RANGE_PER_RANK * r * 100)}% range`
}

export function tempoEffectLabel(rank: number): string {
  const r = clampRank(rank, RESEARCH_MAX_RANK.tempo)
  if (r <= 0) return 'No Tempo research'
  return `+${Math.round(TEMPO_RATE_PER_RANK * r * 100)}% attack rate`
}
